<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ valid, dirty }"
  >
    <v-row
      :rules="{ required: selectedLang === teamMainLang, min: 1, max: 200 }"
      align="center"
      justify="center"
    >
      <v-col>
        <VTextField
          v-model="formData.name[selectedLang]"
          outlined
          :label="$t('forms.labels.name')"
          data-cy="analysis-type-text-name"
          :rules="{ required: selectedLang === teamMainLang, min: 1, max: 200 }"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <VAutocomplete
          v-model="formData.category"
          outlined
          clearable
          :label="$t('forms.labels.category')"
          :items="displayedCategories"
          :loading="loadingCategories"
          data-cy="AnalysisCategory"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <VTextField
          v-model="formData.ERPCode"
          outlined
          :label="$t('forms.labels.ERPCode')"
          :rules="{ min: 1, max: 200 }"
          data-cy="ERPCode"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <VSelect
          v-model="formData.type"
          outlined
          itemValue="key"
          :label="$t('forms.labels.type')"
          :required="true"
          :items="types"
          data-cy="SelectAnalysisType"
          :disabled="disabledOnMultipleCondition"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <v-row v-if="formData.type === WATER_CONSUMPTION_ANALYSIS_TYPE()">
      <v-col
        cols="12"
      >
        <VSelect
          v-model="formData.flowmeterType"
          :label="$t('devices.flowMeters.types.label')"
          :items="flowmeterTypes"
          itemValue="value"
          outlined
          required
          @input="updateData"
        />
      </v-col>
    </v-row>

    <div
      v-for="(option, index) in formData.listTypeOptions"
      :key="index"
    >
      <v-row
        v-if="isListType"
      >
        <v-col cols="10">
          <VTextField
            v-model="formData.listTypeOptions[index][selectedLang]"
            outlined
            :label="$t('forms.labels.option', { lang: selectedLang })"
            :rules="{ required: selectedLang === teamMainLang, max: 200 }"
            @input="updateData"
          />
        </v-col>
        <v-col
          cols="2"
          class="p-3"
        >
          <v-btn
            v-if="index + 1 === listTypeOptionsLength"
            icon
            @click="addRow"
          >
            <v-icon>
              add
            </v-icon>
          </v-btn>
          <v-btn
            icon
            @click="removeRow(index)"
          >
            <v-icon>
              remove
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <v-row>
      <v-col v-if="isSimpleType">
        <VAutocomplete
          v-model="formData.deviceAnalysisKey"
          outlined
          :label="$t('forms.labels.key')"
          :rules="{ min: 1, max: 200 }"
          itemValue="id"
          itemText="key"
          :items="displayedAnalysisKeys"
          clearable
          clearIcon="mdi-close-circle"
          data-cy="key"
          @input="updateAnalysisKeys"
        />
      </v-col>
      <v-col v-if="!isConsumptionAnalysis">
        <VTextField
          v-model="formData.measureUnit[selectedLang]"
          outlined
          :label="$t('forms.labels.measureUnit')"
          :rules="{ min: 1, max: 200, required: isTextOrListType ? false : true }"
          data-cy="unitOfMeasure"
          :disabled="isSimpleType ? !!formData.deviceAnalysisKey : false"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <v-row v-if="isSimpleType && formData.deviceAnalysisKey">
      <v-col>
        <VTextField
          v-model="formData.conversionFactor"
          outlined
          :label="$t('forms.labels.conversionFactor')"
          :rules="{ min: 1, max: 200 }"
          data-cy="conversionFactor"
          type="number"
          @input="updateConversionFactor"
        />
      </v-col>
      <v-col>
        <VTextField
          v-model="formData.reportUnitOfMeasure"
          outlined
          :label="$t('forms.labels.reportUnitOfMeasure')"
          :rules="{required: !!formData.conversionFactor && parseFloat(formData.conversionFactor) !== 1,
                   compareString: [formData.measureUnit[selectedLang]] }"
          data-cy="reportUnitOfMeasure"
          :disabled="!formData.conversionFactor || !formData.deviceAnalysisKey"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <VTextarea
          v-model="formData.description[selectedLang]"
          outlined
          :label="$t('forms.labels.description')"
          data-cy="analysis-type-text-description"
          :rules="{ min: 1, max: 200 }"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="12"
        sm="12"
        md="6"
      >
        <VCheckbox
          v-model="formData.active"
          :label="$t('forms.labels.active')"
          data-cy="typeAndFormula-active"
          @change="updateData"
        />
      </v-col>
      <v-col
        cols="12"
        sm="12"
        md="6"
      >
        <VCheckbox
          v-model="formData.includeInRBQ"
          :label="$t('forms.labels.includeInRBQ')"
          data-cy="typeAndFormula-includeInRBQ"
          @change="updateData"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
      >
        <VAutocomplete
          v-model="formData.workOrderTypes"
          outlined
          clearable
          :multiple="true"
          :label="$t('forms.labels.workOrderType')"
          :items="displayedWorkOrderTypes"
          :loading="loadingWorkorderTypes"
          data-cy="addAnalyseType_typeAndFormula_WOType"
          @input="updateData"
        />
      </v-col>
      <v-col cols="12">
        <VCheckbox
          v-if="isCalculatedType || isSimpleType"
          v-model="formData.condition"
          :label="conditionLabel"
          @change="updateData"
        />
      </v-col>
      <v-col />
    </v-row>

    <v-row v-if="isCalculatedType">
      <v-col cols="9">
        <VTextField
          v-model="formData.formula"
          outlined
          :label="$t('forms.labels.formula')"
          :rules="{ required: true, max: 250 }"
          @input="updateData"
          @blur="upperCaseFormula"
        />
      </v-col>

      <div class="formula-hint">
        <v-icon @click="triggerDisplayModal">
          mdi-information-outline
        </v-icon>

        <InformationModal
          v-show="isModalVisible"
          @close="triggerDisplayModal"
        />
      </div>
    </v-row>

    <v-row v-if="isCalculatedType">
      <v-col cols="12">
        <DefinitionOfVariables
          :list="formData.variablesName"
          @onChange="updateVariables"
        />
      </v-col>
    </v-row>

    <v-row v-if="formData.type === 'SimpleAnalysisType' && isMassBalanceActivated">
      <v-col
        cols="12"
      >
        <VCheckbox
          v-model="formData.massBalance"
          :label="$t('clientPage.massBalance')"
          @change="updateData"
        />
        <VAutocomplete
          v-if="formData.massBalance"
          v-model="formData.componentTypes"
          :items="massBalanceComponentTypes"
          :label="$t('forms.labels.massBalanceType')"
          outlined
          :rules="{ required: true}"
          :clearable="true"
          multiple
          @input="updateData"
        />
      </v-col>
    </v-row>
    <SlotScopeWatcher
      :value="dirty"
      @change="$emit('dirty-changed', dirty)"
    />
    <SlotScopeWatcher
      :value="valid"
      @change="$emit('valid-changed', valid)"
    />
  </ValidationObserver>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import { isPlainObject } from 'lodash';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import { TYPE_MAKE_UP, TYPE_BLOWDOWN, TYPE_FEED, TYPE_STEAM, TYPE_CONDENSATE } from '@/aquassist-shared/constants/types/flowMeters';
import {
    SIMPLE_ANALYSIS_TYPE,
    CALCULATED_ANALYSIS_TYPE,
    WATER_CONSUMPTION_ANALYSIS_TYPE,
    PRODUCT_CONSUMPTION_ANALYSIS_TYPE,
    TYPE_COMPOSITION,
    TYPE_CORROSION,
    TYPE_LEGIONELLA,
    TYPE_TEXT,
    TYPE_LIST,
    FUEL_CONSUMPTION_ANALYSIS_TYPE,
    FLOW_METER_ANALYSIS_TYPE,
} from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import { TYPE_MASS_BALANCE } from '@/aquassist-shared/constants/types/features';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import InformationModal from './InformationModal.vue';
import DefinitionOfVariables from './DefintionOfVariables.vue';

export default {
    components: {
        ValidationObserver,
        SlotScopeWatcher,
        VAutocomplete,
        VTextField,
        VSelect,
        VTextarea,
        InformationModal,
        DefinitionOfVariables,
    },
    props: {
        analysisType: {
            type: Object,
            default: () => ({}),
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        components: [],
        categories: [],
        workOrderTypes: [],
        isMassBalanceActivated: false,
        formData: {
            name: {},
            description: {},
            deviceAnalysisKey: null,
            measureUnit: {},
            massBalance: false,
            conversionFactor: null,
            reportUnitOfMeasure: null,
            listTypeOptions: [{}],

        },
        isModalVisible: false,
        disabledTypeFieldByDefault: false,

        loadingCategories: false,
        loadingWorkorderTypes: false,
        // Mass Balance
        includeMassBalance: false,
        // DeviceAnalysisKeys
        deviceAnalysisKeys: [],


    }),
    computed: {
        ...mapGetters([
            'activeTab',
            'teamLangs',
            'userLang',
            'lang',
            'selectedLang',
            'hasRole',
            'teamMainLang',
            'teamID',
        ]),
        displayedCategories() {
            return this.categories.map(category => ({
                id: category.id,
                name: category.name[this.userLang] || category.name[this.teamMainLang],
            }));
        },

        displayedAnalysisKeys() {
            return this.deviceAnalysisKeys.map(({ id, key }) => ({ id, key }));
        },

        displayedWorkOrderTypes() {
            return this.workOrderTypes.map(type => ({
                id: type.id,
                name: type.name[this.userLang] || type.name[this.teamMainLang],
            }));
        },
        flowmeterTypes() {
            return [TYPE_MAKE_UP, TYPE_BLOWDOWN, TYPE_FEED, TYPE_STEAM, TYPE_CONDENSATE].map(type => ({
                name: this.$t(`devices.flowMeters.types.${type}`),
                value: type,
            }));
        },
        isCalculatedOrSimpleAnalysis() {
            return [SIMPLE_ANALYSIS_TYPE, CALCULATED_ANALYSIS_TYPE].includes(this.analysisType.type);
        },
        types() {
            // we are editing
            // manager or laboratory manager can change these type only. simple and canlculated

            const populatedTypes = [{
                name: this.$t(`analyses.types.${SIMPLE_ANALYSIS_TYPE}`),
                key: SIMPLE_ANALYSIS_TYPE,
            }, {
                name: this.$t(`analyses.types.${CALCULATED_ANALYSIS_TYPE}`),
                key: CALCULATED_ANALYSIS_TYPE,
            }];
            if (!this.isCalculatedOrSimpleAnalysis) {
                [WATER_CONSUMPTION_ANALYSIS_TYPE, PRODUCT_CONSUMPTION_ANALYSIS_TYPE, FLOW_METER_ANALYSIS_TYPE, TYPE_COMPOSITION, TYPE_CORROSION,
                    TYPE_LEGIONELLA, TYPE_TEXT, TYPE_LIST, FUEL_CONSUMPTION_ANALYSIS_TYPE].forEach(type => {
                    populatedTypes.push({
                        name: this.$t(`analyses.types.${type}`),
                        key: type,
                    });
                });
            }

            return populatedTypes;
        },
        massBalanceComponentTypes() {
            const types = [
                {
                    name: 'cooling',
                    key: 'cooling',
                },
                {
                    name: 'boiler',
                    key: 'boiler',
                },
            ];
            return types;
        },
        isSimpleType() {
            return this.formData.type === SIMPLE_ANALYSIS_TYPE;
        },
        isCalculatedType() {
            return this.formData.type === CALCULATED_ANALYSIS_TYPE;
        },
        isTextOrListType() {
            return (this.formData.type === TYPE_LIST || this.formData.type === TYPE_TEXT);
        },
        isListType() {
            return this.formData.type === TYPE_LIST;
        },
        isConsumptionAnalysis() {
            return (this.formData.type === WATER_CONSUMPTION_ANALYSIS_TYPE || this.formData.type === PRODUCT_CONSUMPTION_ANALYSIS_TYPE
                || this.formData.type === FUEL_CONSUMPTION_ANALYSIS_TYPE || this.formData.type === FLOW_METER_ANALYSIS_TYPE);
        },
        conditionLabel() {
            if (this.isCalculatedType) {
                return this.$t('analyses.types.condition.calculated');
            }

            return this.$t('analyses.types.condition.simple');
        },
        listTypeOptionsLength() {
            return this.formData.listTypeOptions.length;
        },
        disabledOnMultipleCondition() {
            // On desactive le champs pour tout le monde si c'est un water consumption ou un product consumption
            if (this.disabledTypeFieldByDefault) {
                return true;
            }

            // Si on est en mode creation, on peut afficher et changer le type
            if (!this.isEdition) {
                return false;
            }
            // Si on est admin, on peut tout changer tout le temps
            if (this.hasRole('administrator') || this.hasRole('manager')) {
                return false;
            }
            // Un manager ou labManager peut changer uniquement le type si c'est une analyse calculée
            // De simple à calculée ou de calculée à simple
            if (this.isCalculatedOrSimpleAnalysis && ((this.hasRole('manager')) || this.hasRole('laboratoryManager'))) {
                return false;
            }
            // Dans tous les autres cas, on ne peut pas changer le type
            // Si d'autres cas viennent se rajouter, on peut les ajouter à la suite ici
            return true;
        },
    },
    async created() {
        await this.getCategories();
        await this.getWorkOrderTypes();
        await this.getAllKeys();
        await this.checkMassBalanceFeatureForTeam();
        if (this.isEdition) {
            this.formData = this.prepareData(this.analysisType);
            this.data = this.category;
        }
        // Desactiver le champ type si le type est un  "Water consumption" ou "Product consumption"
        if (this.isConsumptionAnalysis) {
            this.disabledTypeFieldByDefault = true;
        }
    },
    mounted() {
        // Validate at start
        if (this.isEdition) {
            this.$refs.observer.validate();
        }
    },

    methods: {
        prepareData(analysisType) {
            const { category, id, ...data } = analysisType;

            if (isPlainObject(category)) {
                data.category = category.id;
            }

            const {
                active,
                measureUnit,
                listTypeOptions,
                workOrderTypes,
                condition,
                formula,
                variablesName,
                type,
                flowmeterType,
                includeInRBQ,
                massBalance,
                componentTypes,
            } = analysisType;

            // Have to merge default data, being able to switch type
            return {
                ...this.formData,
                ...data,
                active,
                measureUnit: measureUnit || {},
                listTypeOptions,
                workOrderTypes,
                condition,
                formula,
                variablesName,
                type,
                flowmeterType,
                includeInRBQ,
                massBalance,
                componentTypes,
            };
        },
        WATER_CONSUMPTION_ANALYSIS_TYPE() {
            return WATER_CONSUMPTION_ANALYSIS_TYPE;
        },
        PRODUCT_CONSUMPTION_ANALYSIS_TYPE() {
            return PRODUCT_CONSUMPTION_ANALYSIS_TYPE;
        },
        tableHeaders() {
            const headers = this.components.map((component, index) => ({
                index,
                name: this.$t(`analysisTypes.form.step${index}.title`),
                component: Vue.component(`tab-${index}`, component),
            }));
            this.$store.dispatch('setTabs', headers);
            return headers;
        },
        updateData() {
            this.$emit('sendData', this.formData);
        },
        async getCategories() {
            this.loadingCategories = true;
            const { data } = await this.$rq.getAnalysisCategories();
            this.categories = data.categories;
            this.loadingCategories = false;
        },
        async getWorkOrderTypes() {
            this.loadingWorkorderTypes = true;
            const { data } = await this.$rq.getWorkOrderTypes();
            this.workOrderTypes = data;
            this.loadingWorkorderTypes = false;
        },
        addRow() {
            this.formData.listTypeOptions.push({});
        },
        removeRow(index) {
            this.formData.listTypeOptions.splice(index, 1);
            this.updateData();
        },
        updateFormData() {
            if (!this.formData.massBalance) {
                this.formData.componentTypes = [];
                this.updateData();
            }
        },
        triggerDisplayModal() {
            this.isModalVisible = !this.isModalVisible;
            this.updateData();
        },
        async getAllKeys() {
            const { data } = await this.$rq.getDeviceAnalysisKeys({ pagination: false });
            this.deviceAnalysisKeys = data.deviceAnalysisKeys;
        },

        clearDeviceAnalysisKey() {
            this.formData.deviceAnalysisKey = null;
            this.formData.measureUnit = {};
            this.formData.conversionFactor = '';
            this.formData.reportUnitOfMeasure = '';
            this.updateData();
        },
        updateAnalysisKeys(item) {
            // clear the device analysis key
            if (!item) {
                return this.clearDeviceAnalysisKey();
            }
            const { unit } = this?.deviceAnalysisKeys?.find(key => key.id === item);
            this.formData.measureUnit = unit;
            if (!unit) {
                this.formData.measureUnit = {};
                this.formData.conversionFactor = '';
                this.formData.reportUnitOfMeasure = '';
            }
            return this.updateData();
        },
        updateVariables(variables) {
            this.formData = {
                ...this.formData,
                variablesName: variables,
            };
            this.updateData();
        },
        updateConversionFactor() {
            if (!this.formData.conversionFactor) {
                this.formData.reportUnitOfMeasure = null;
            }
            this.updateData();
        },
        async checkMassBalanceFeatureForTeam() {
            const { data: team } = await this.$rq.getTeam(this.teamID);
            this.isMassBalanceActivated = team?.activeFeatures.some(feature => feature.feature.type === TYPE_MASS_BALANCE);
        },
        upperCaseFormula() {
            // Convert only single-letter alphabetic characters to uppercase, skip all math expression in this list
            // eslint-disable-next-line max-len
            const regex = /(?<!\w)(?<!abs)(?<!log10)(?<!log2)(?<!log1p)(?<!log)(?<!ln)(?<!pow)(?<!sin)(?<!cos)(?<!tan)(?<!asin)(?<!acos)(?<!atan)(?<!sinh)(?<!cosh)(?<!tanh)(?<!asinh)(?<!acosh)(?<!atanh)(?<!sqrt)(?<!ln)(?<!lg)(?<!ceil)(?<!floor)(?<!round)(?<!trunc)(?<!exp)(?<!length)(?<!in)(?<!random)(?<!min)(?<!max)(?<!assignment)(?<!fndef)(?<!cbrt)(?<!expm1)(?<!sign)([a-zA-Z])(?!\w)/g;
            this.formData.formula = this.formData.formula.replace(regex, match => match.toUpperCase());

            // lowercase function names
            const functionNames = [
                'sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'sinh', 'cosh', 'tanh', 'asinh', 'acosh', 'atanh',
                'sqrt', 'log', 'ln', 'lg', 'log10', 'abs', 'ceil', 'floor', 'round', 'trunc', 'exp', 'length', 'in',
                'random', 'min', 'max', 'assignment', 'fndef', 'cbrt', 'expm1', 'log1p', 'sign', 'log2', 'pow',
            ];

            // Build a regex to match function names, case-insensitive, as whole words
            const funcPattern = functionNames.sort((a, b) => b.length - a.length).join('|');
            const funcRegex = new RegExp(`\\b(${funcPattern})\\b`, 'gi');
            this.formData.formula = this.formData.formula.replace(funcRegex, match => match.toLowerCase());
        },
    },
};
</script>

<style lang="scss" scoped>
    .formula-hint {
        position: relative;
    }
</style>
